/**
 * Voice layer contracts (PR5) — PURE types, no React, no network, no store.
 *
 * Two provider families:
 *   - {@link SpeechRecognitionProvider} (ASR / speech-to-text)
 *   - {@link SpeechSynthesisProvider}   (TTS / text-to-speech)
 *
 * Errors are normalized to a stable {@link VoiceErrorCode} union and carry an
 * i18n KEY (e.g. `voice.errors.notAllowed`) — never a translated string. The
 * translation lands in PR8 locales; this layer only returns the key.
 */

/**
 * Stable, provider-agnostic error taxonomy. Browser-specific error strings
 * (`not-allowed`, `NotReadableError`, …) are mapped onto these by the
 * concrete providers so the rest of the app never branches on vendor codes.
 */
export type VoiceErrorCode =
  | 'not-supported'
  | 'not-allowed'
  | 'no-speech'
  | 'audio-capture'
  | 'aborted'
  | 'network'
  | 'service-not-allowed'
  | 'language-not-supported'
  | 'synthesis-failed'
  | 'unknown'

/**
 * Codes that are EXPECTED during normal hands-free use and must NOT be surfaced
 * as an error toast or turned into an empty turn by the consumer (PR8):
 *   - `no-speech` / `aborted`: the user simply did not speak, or barged in.
 * Everything else (`not-allowed`, `audio-capture`, `service-not-allowed`,
 * `not-supported`, `network`, `language-not-supported`, `synthesis-failed`,
 * `unknown`) is a genuine fault the user should see.
 */
const RECOVERABLE_CODES: ReadonlySet<VoiceErrorCode> = new Set<VoiceErrorCode>([
  'no-speech',
  'aborted',
])

/**
 * Maps every {@link VoiceErrorCode} to its canonical i18n key. The actual
 * translations are provided in PR8; consumers pass `error.i18nKey` to `t()`.
 */
export const VOICE_ERROR_I18N_KEY: Record<VoiceErrorCode, string> = {
  'not-supported': 'voice.errors.notSupported',
  'not-allowed': 'voice.errors.notAllowed',
  'no-speech': 'voice.errors.noSpeech',
  'audio-capture': 'voice.errors.audioCapture',
  aborted: 'voice.errors.aborted',
  network: 'voice.errors.network',
  'service-not-allowed': 'voice.errors.serviceNotAllowed',
  'language-not-supported': 'voice.errors.languageNotSupported',
  'synthesis-failed': 'voice.errors.synthesisFailed',
  unknown: 'voice.errors.unknown',
}

/**
 * Normalized voice error. `i18nKey` is the user-facing message key; `cause`
 * preserves the original browser/vendor string for telemetry only.
 */
export class VoiceError extends Error {
  readonly code: VoiceErrorCode
  /** i18n KEY (not a translation), e.g. `voice.errors.notAllowed`. */
  readonly i18nKey: string
  /** Original vendor error string, if any — for logs/telemetry only. */
  readonly vendorCode?: string
  /**
   * `true` for codes expected during normal hands-free use (`no-speech`,
   * `aborted`) — the consumer should treat these as silence, NOT as a toast or
   * an empty turn. `false` for genuine faults the user should see.
   */
  readonly recoverable: boolean

  constructor(code: VoiceErrorCode, vendorCode?: string) {
    const i18nKey = VOICE_ERROR_I18N_KEY[code]
    super(i18nKey)
    this.name = 'VoiceError'
    this.code = code
    this.i18nKey = i18nKey
    this.vendorCode = vendorCode
    this.recoverable = RECOVERABLE_CODES.has(code)
    // Restore prototype chain (TS target ES2022 + extending Error).
    Object.setPrototypeOf(this, VoiceError.prototype)
  }
}

/** Type guard for {@link VoiceError}. */
export function isVoiceError(value: unknown): value is VoiceError {
  return value instanceof VoiceError
}

/** A single recognition hypothesis emitted by an ASR provider. */
export interface SpeechRecognitionResult {
  /** Recognized text. */
  transcript: string
  /** `true` once the engine commits the phrase; interim results are `false`. */
  isFinal: boolean
  /** Engine confidence in `[0, 1]` when available; `undefined` otherwise. */
  confidence?: number
}

/** Callbacks for a recognition session. */
export interface SpeechRecognitionCallbacks {
  /** Fired for every result, interim and final. */
  onResult: (result: SpeechRecognitionResult) => void
  /** Fired once per session when the engine reports a (normalized) error. */
  onError: (error: VoiceError) => void
  /** Fired when the engine starts capturing audio. */
  onStart?: () => void
  /** Fired when the session ends (natural end, stop(), or abort()). */
  onEnd?: () => void
}

/** Options for a recognition session. */
export interface SpeechRecognitionOptions {
  /** BCP-47 language tag. Defaults to `pt-BR`. */
  lang?: string
  /** Emit interim (non-final) results. Defaults to `true`. */
  interimResults?: boolean
  /** Keep listening across pauses. Defaults to `false` (single utterance). */
  continuous?: boolean
}

/** A microphone permission state, mirroring the Permissions API vocabulary. */
export type MicrophonePermissionState = 'granted' | 'denied' | 'prompt' | 'unknown'

/**
 * ASR provider contract. Implementations are stateful (one active session at a
 * time) but own no React/store state — callers wire them into stores in PR8.
 */
export interface SpeechRecognitionProvider {
  /** Stable identifier, e.g. `'web-speech'`, `'whisper'`. */
  readonly id: string
  /** `true` when this provider can run in the current environment. */
  isSupported(): boolean
  /**
   * Begin a recognition session. Calling `start` while a session is active is
   * a no-op (the existing session keeps running).
   */
  start(callbacks: SpeechRecognitionCallbacks, options?: SpeechRecognitionOptions): void
  /** Stop gracefully — the engine flushes a final result, then `onEnd` fires. */
  stop(): void
  /** Abort immediately — no final result; `onEnd` fires. */
  abort(): void
}

/** Options for a single synthesis utterance. */
export interface SpeechSynthesisOptions {
  /** BCP-47 language tag. Defaults to `pt-BR`. */
  lang?: string
  /** Speech rate multiplier. Defaults to `1`. */
  rate?: number
  /** Pitch multiplier. Defaults to `1`. */
  pitch?: number
}

/**
 * TTS provider contract. Utterances are queued serially; barge-in is achieved
 * via {@link SpeechSynthesisProvider.cancelAll}.
 */
export interface SpeechSynthesisProvider {
  /** Stable identifier, e.g. `'web-speech'`. */
  readonly id: string
  /** `true` when this provider can run in the current environment. */
  isSupported(): boolean
  /**
   * Arm narration with a user gesture (required on iOS Safari before audio
   * can play). Idempotent; safe to call on every tap.
   */
  arm(): void
  /** `true` once {@link arm} has run successfully. */
  isArmed(): boolean
  /** Enqueue text for serial playback. Resolves when this utterance ends. */
  speak(text: string, options?: SpeechSynthesisOptions): Promise<void>
  /** Barge-in: cancel the current utterance and clear the queue. */
  cancelAll(): void
  /** `true` while an utterance is playing or queued. */
  isSpeaking(): boolean
}
